import React, { Suspense } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router, Route, Switch, useLocation } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import HelpButton from './components/HelpButton';
import LanguageSwitcher from './components/shared/LanguageSwitcher';

const AdminLogin = React.lazy(() => import('./components/admin/AdminLogin'));
const AdminRegister = React.lazy(() => import('./components/admin/AdminRegister'));
const HomeMenu = React.lazy(() => import('./components/HomeMenu'));
const Eventos = React.lazy(() => import('./components/admin/Eventos'));
const Mesas = React.lazy(() => import('./components/admin/Mesas'));
const Canciones = React.lazy(() => import('./components/admin/Canciones'));
const Karaoke = React.lazy(() => import('./components/Karaoke'));
const KaraokeAutomatico = React.lazy(() => import('./components/KaraokeAutomatico'));
const KaraokeManual = React.lazy(() => import('./components/KaraokeManual'));
const Videos = React.lazy(() => import('./components/Videos'));
const ClientLogin = React.lazy(() => import('./pages/ClientLogin'));
const ClientRoutesLayout = React.lazy(() => import('./components/client/ClientRoutesLayout'));
const HomePage = React.lazy(() => import('./components/HomePage'));
const SeoTurnosLanding = React.lazy(() => import('./components/SeoTurnosLanding'));
const AdminOrdenKaraoke = React.lazy(() => import('./components/admin/AdminOrdenKaraoke'));
const SimulacionRotacion = React.lazy(() => import('./components/admin/SimulacionRotacion'));
const ReporteEvento = React.lazy(() => import('./components/admin/ReporteEvento'));
const AdminReacciones = React.lazy(() => import('./components/admin/AdminReacciones'));
const Ayuda = React.lazy(() => import('./components/ayuda'));
const SubscriptionPlans = React.lazy(() => import('./pages/SubscriptionPlans'));
const BillingDashboard = React.lazy(() => import('./pages/BillingDashboard'));
const PurchaseEvents = React.lazy(() => import('./pages/PurchaseEvents'));
const BrandingSettings = React.lazy(() => import('./pages/BrandingSettings'));
const SubscriptionSuccess = React.lazy(() => import('./pages/SubscriptionSuccess'));
const PaymentSuccess = React.lazy(() => import('./pages/PaymentSuccess'));
const SubscriptionCancel = React.lazy(() => import('./pages/SubscriptionCancel'));
const PaymentCancel = React.lazy(() => import('./pages/PaymentCancel'));
const VerifyEmail = React.lazy(() => import('./pages/VerifyEmail'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const BecomeHost = React.lazy(() => import('./pages/BecomeHost'));
const SuperAdminPanel = React.lazy(() => import('./pages/SuperAdminPanel'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

const App: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const getHelpSection = () => {
    const path = location.pathname;
    if (path === '/') return 'landing';
    if (path === '/home') return 'home';
    if (path === '/become-host') return 'become-host';
    if (path === '/login') return 'client-login';
    if (path === '/admin/login') return 'admin-login';
    if (path === '/admin/register') return 'admin-register';
    if (path === '/admin/forgot-password') return 'forgot-password';
    if (path.startsWith('/admin/reset-password')) return 'reset-password';
    if (path.startsWith('/admin/verify-email')) return 'verify-email';
    if (path.startsWith('/admin/eventos/') && path.endsWith('/reporte')) return 'reporte-evento';
    if (path.startsWith('/admin/eventos')) return 'eventos';
    if (path.startsWith('/admin/mesas')) return 'mesas';
    if (path.startsWith('/admin/canciones')) return 'canciones';
    if (path.startsWith('/admin/orden-karaoke')) return 'orden-karaoke';
    if (path.startsWith('/admin/reacciones')) return 'reacciones';
    if (path.startsWith('/videos')) return 'videos';
    if (path.startsWith('/admin')) return 'admin';
    if (path.startsWith('/client/menu')) return 'client';
    if (path.startsWith('/client/pedir-canciones')) return 'pedir-canciones';
    if (path.startsWith('/client/reacciones')) return 'mandar-reacciones';
    if (path.startsWith('/client/orden-karaoke')) return 'orden-karaoke-cliente';
    if (path.startsWith('/client/compartir-qr')) return 'compartir-qr';
    if (path.startsWith('/client/encuesta')) return 'encuesta';
    if (path === '/subscription-success') return 'subscription-success';
    if (path === '/payment-success') return 'payment-success';
    if (path === '/subscription-cancel') return 'subscription-cancel';
    if (path === '/payment-cancel') return 'payment-cancel';
    if (path.startsWith('/subscription-plans')) return 'subscription-plans';
    if (path.startsWith('/billing-dashboard')) return 'billing-dashboard';
    if (path.startsWith('/purchase-events')) return 'purchase-events';
    if (path.startsWith('/branding-settings')) return 'branding-settings';
    if (path.startsWith('/karaoke-automatico')) return 'karaoke-automatico';
    if (path.startsWith('/karaoke-manual')) return 'karaoke-manual';
    if (path.startsWith('/karaoke')) return 'karaoke';
    return 'general';
  };

  const path = location.pathname;
  const helpButtonClassName = path.startsWith('/client/') ? 'help-button--above-promo' : '';

  return (
    <div className="App">
      <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>{t('common.loading')}</div>}>
        <Switch>
          {/* Rutas públicas (es) */}
          <Route exact path="/" render={() => <HomePage routeLang="es" />} />
          <Route
            exact
            path="/sistema-administracion-turnos-karaoke"
            render={() => <SeoTurnosLanding routeLang="es" />}
          />

          {/* Rutas públicas (en) - solo páginas indexables */}
          <Route exact path="/en" render={() => <HomePage routeLang="en" />} />
          <Route
            exact
            path="/en/karaoke-queue-management-system"
            render={() => <SeoTurnosLanding routeLang="en" />}
          />

          <Route path="/admin/login" component={AdminLogin} />
          <Route path="/admin/register" component={AdminRegister} />
          <Route path="/login" component={ClientLogin} />
          
          {/* Rutas de Magic Link - Email verification y password recovery */}
          <Route path="/admin/verify-email/:token" component={VerifyEmail} />
          <Route path="/admin/forgot-password" component={ForgotPassword} />
          <Route path="/admin/reset-password/:token" component={ResetPassword} />
          
          {/* Rutas protegidas de administración */}
          <ProtectedRoute path="/home" component={HomeMenu} />
          <ProtectedRoute path="/admin/cuentas" component={SuperAdminPanel} />
          <ProtectedRoute path="/admin/eventos/:eventoId/reporte" component={ReporteEvento} />
          <ProtectedRoute path="/admin/eventos" component={Eventos} />
          <ProtectedRoute path="/admin/mesas" component={Mesas} />
          <ProtectedRoute path="/admin/simulacion-rotacion" component={SimulacionRotacion} />
          <ProtectedRoute path="/admin/canciones" component={Canciones} />
          <ProtectedRoute path="/admin/orden-karaoke/:eventoId" component={AdminOrdenKaraoke} />
          <ProtectedRoute path="/admin/reacciones" component={AdminReacciones} />
          <ProtectedRoute path="/karaoke-automatico" component={KaraokeAutomatico} />
          <ProtectedRoute path="/karaoke-manual" component={KaraokeManual} />
          <ProtectedRoute path="/karaoke" component={Karaoke} />
          <ProtectedRoute path="/videos" component={Videos} />
          <ProtectedRoute path="/subscription-plans" component={SubscriptionPlans} />
          <ProtectedRoute path="/billing-dashboard" component={BillingDashboard} />
          <ProtectedRoute path="/purchase-events" component={PurchaseEvents} />
          <ProtectedRoute path="/branding-settings" component={BrandingSettings} />
          <ProtectedRoute path="/subscription-success" component={SubscriptionSuccess} />
          <ProtectedRoute path="/payment-success" component={PaymentSuccess} />
          <ProtectedRoute path="/subscription-cancel" component={SubscriptionCancel} />
          <ProtectedRoute path="/payment-cancel" component={PaymentCancel} />
          
          {/* Rutas de cliente con colores de personalización scoped */}
          <Route path="/client" component={ClientRoutesLayout} />
          
          {/* Promotional pages */}
          <Route path="/become-host" component={BecomeHost} />
          
          {/* Ayuda (accesible sin autenticación) */}
          <Route path="/ayuda/:section?" component={Ayuda} />

          {/* 404: cualquier ruta no declarada arriba (noindex para buscadores) */}
          <Route component={NotFound} />
        </Switch>
      </Suspense>
      {/* Portal a body: evita que un ancestro invalide position:fixed (el toggle
          terminaba abajo del documento en algunos WebViews móviles). */}
      {!path.startsWith('/home') &&
        createPortal(
          <LanguageSwitcher className="app-global-language" variant="compact" />,
          document.body
        )}
      {!location.pathname.startsWith('/ayuda') && (
        <HelpButton section={getHelpSection()} className={helpButtonClassName} />
      )}
    </div>
  );
};

const AppWrapper: React.FC = () => (
  <Router>
    <App />
  </Router>
);

export default AppWrapper;
